import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Password is required");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data.success) {
        sessionStorage.setItem("bs_admin", "true");
        navigate("/admin/dashboard");
      }
    } catch (err) {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center relative">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="w-full max-w-sm px-6"
      >
        <div className="text-center mb-12">
          <img
            src="https://blanksage-public-assets.s3.ap-south-1.amazonaws.com/bs+only+logo.png"
            alt="Blanksage"
            className="h-10 mx-auto mb-8 brightness-0 invert opacity-60"
            data-testid="admin-login-logo"
          />
          <h1 className="text-2xl font-bold text-white tracking-tight mb-2">
            Admin Portal
          </h1>
          <p className="text-zinc-600 text-sm font-light">
            Enter password to access the dashboard
          </p>
        </div>

        <form onSubmit={handleSubmit} data-testid="admin-login-form" className="space-y-8">
          <div>
            <Label className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.2em] mb-4 block">
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-0 top-4 w-4 h-4 text-zinc-700" />
              <Input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="h-14 bg-transparent border-0 border-b border-zinc-800 rounded-none focus:border-white focus:ring-0 pl-7 text-lg text-white placeholder:text-zinc-700 transition-colors duration-300"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="h-14 w-full bg-white text-black hover:bg-[#BEF264] hover:text-black font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 group disabled:opacity-50"
          >
            {loading ? (
              <span className="flex items-center gap-3 justify-center">
                <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Verifying
              </span>
            ) : (
              <span className="flex items-center gap-3 justify-center">
                Access Dashboard
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            )}
          </Button>
        </form>

        <div className="mt-12 text-center">
          <a
            href="/"
            className="text-zinc-700 hover:text-zinc-400 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300"
            data-testid="back-to-careers-link"
          >
            Back to Careers
          </a>
        </div>
      </motion.div>
    </div>
  );
}
